import { PageOrThread } from '@/components/admin/experiences/conversation-thread';
import { PAGE_EDITOR_IDS } from '@/components/admin/experiences/page-editor/contracts';
import { ConversationBadge } from '@/components/admin/experiences/timeline/conversation-badge';
import { ExperienceRail } from '@/components/admin/experiences/timeline/experience-rail';
import { FeedbackBadge } from '@/components/admin/experiences/timeline/feedback-badge';
import { DISABLED_INERT } from '@/components/ui/button';
import { edgeInThread } from '@/lib/experience-draft';
import { IN_THREAD_LABEL } from '@/lib/experience-labels';
import type { PageNeighbours } from '@/lib/page-editor';
import { SPEED_LABEL } from '@/lib/page-editor';
import { cn } from '@/lib/utils';
import type {
  ExperiencePageDraft,
  ExperienceTheme,
  ExperienceVariable,
  PageTransition,
} from '@matter_extractor/shared';
import { isConversationPage } from '@matter_extractor/shared';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';

/**
 * Le bandeau de navigation entre pages (refonte modulaire §9.3; planche
 * `EditeurPageEcran`; issues #58 et #140).
 *
 * « La page précédente et la page suivante, NOMMÉES, la suivante avec une
 * mini-vignette ; au début de la ligne, la précédente se lit "Début de
 * l'expérience", inactive. Entre elles, une ligne qui porte la position et la
 * transition vers la suivante. »
 *
 * ## Le RAIL, au-dessus de cette ligne (issue #140)
 *
 * Two neighbours are only two: going from page 1 to page 7 cost six clicks on
 * « Suivante », which Alice reported on the 2026-09-23. The component that
 * answers it already existed - `ExperienceRail`, whose docstring says it is
 * « la seule façon d'atteindre la page 7 depuis la page 2 sans traverser les
 * cinq » - and it was mounted in the Timeline tab alone. So it is posted here
 * as well, in its compact variant, and it stays ONE component: a second frieze
 * drawn apart would end up diverging from the first.
 *
 * **Where exactly**, among the three placements Alice was shown: the middle
 * column becomes a STACK OF TWO ROWS, the rail on top and under it the line
 * « Page 3 sur 7 · transition fondu · normal », which thereby keeps its place.
 * The banner grows by some thirty pixels, against the forty a separate row
 * above it would have cost.
 *
 * The column carries `min-w-0`, and that is not decorative: the rail scrolls in
 * its own box (thirty pages fit no width), and a flex child stays at
 * `min-width: auto` until it is told otherwise - the box would then grow with
 * its content instead of clipping it, and it is the BODY that would scroll
 * sideways. `admin-responsive.spec.ts` holds the whole administration to zero
 * horizontal overflow on a phone.
 *
 * It carries `basis-64` as WELL, and the pair is what makes it readable at both
 * ends: the two neighbour groups measure close to a thousand pixels together,
 * so a column based at zero would have let itself be squeezed to a hundred on a
 * laptop - a rail of two pastilles, which is the defect being repaired. With a
 * basis, the banner's `flex-wrap` sends it to a line of its own, where it takes
 * the whole width; `min-w-0` then lets it shrink under that basis on a phone,
 * where nothing may overflow.
 *
 * ## La vignette est le VRAI lecteur, en `static`
 *
 * The mockup draws the next page as a thumbnail, and a thumbnail drawn any
 * other way would be a second renderer of a page - the very thing §9.2 forbids.
 * So it is `PageReader` in `static`, which is inert end to end: nothing reacts,
 * nothing plays, nothing is fetched. It is scaled by a transform rather than by
 * a narrower reading column, because the point of a thumbnail is that it looks
 * like the page and not like a smaller page.
 *
 * The rail mounts a SECOND one of them, in a pastille's bubble, for the very
 * same reason. The 64 x 44 stamp of « Suivante » does not move for all that,
 * and « Précédente » gains none: what the rail adds is a thumbnail ON DEMAND,
 * mounted only while a bubble is open.
 *
 * ## Les deux « + » d'insertion, et pourquoi « avant » reste actif sur la
 * première page
 *
 * §9.3 in as many words: « un "+" de chaque côté pour insérer une page avant ou
 * après celle-ci, qui s'intercale s'il y a déjà des pages de ce côté ; au début
 * de la ligne, la précédente se lit "Début de l'expérience", inactive, et
 * l'insertion avant RESTE POSSIBLE ». The plate draws exactly that - page 1 of
 * 4, the previous neighbour greyed and « Insérer une page avant » live beside
 * it - and it is the one gesture that puts a page ahead of the first one, which
 * « Ajouter une page » (the end of the line) cannot do.
 *
 * What DOES close them is the ceiling: `insertPageAt` refuses past
 * `EXPERIENCE_PAGES_MAX` and answers the draft unchanged, so a button that
 * stayed live there would be a click that does nothing. `canInsert` says so,
 * and it closes both at once - which side you aim at changes nothing about the
 * count.
 *
 * They are NOT a second spelling of « insérer une page »: the caller splices
 * with `insertPageAt`, the very function the scene of #57 calls, and this file
 * only knows which GAP each side aims at.
 *
 * **It is also why the rail here carries no « Ajouter une page »** (#140):
 * these two buttons aim at a PRECISE side, and a third add button would be the
 * same gesture, less precise.
 *
 * ## La page de retour (retour-experience.md §7.1, issue #176)
 *
 * Its « Retour » pastille follows the position line, and the « + » AFTER it is
 * gone: nothing is placed behind the feedback page, and `insertPageAt` would
 * only have answered by putting the page before it. The « + » BEFORE it stays,
 * and so does the one after the page that comes right before it (both insert a
 * content page ahead of the retour).
 *
 * ## La transition se lit ici et se règle dans le panneau « Page »
 *
 * « Le bandeau montre, le panneau règle » (maquette README). So this is a
 * sentence and not a control: a transition set in two places would be two
 * controls an operator has to keep in agreement.
 */

export interface PageNavProps {
  readonly neighbours: PageNeighbours;
  /** The whole line, for the rail (#140) - the scene of the Timeline takes it too. */
  readonly pages: readonly ExperiencePageDraft[];
  /** The page being EDITED: the pastille that carries its name, and the one scrolled to. */
  readonly pageId: string;
  /** « Retour » next to the position when the page being edited is the feedback page (§7.1). */
  readonly feedback?: boolean;
  readonly transition: PageTransition;
  readonly theme: ExperienceTheme;
  readonly format: 'phone' | 'screen';
  /** A version opened by « Voir », or a retired experience: nothing is inserted. */
  readonly readOnly: boolean;
  /** False at the ceiling of `EXPERIENCE_PAGES_MAX` - see the head. */
  readonly canInsert: boolean;
  readonly onGo: (pageId: string) => void;
  /** Splices a page into the gap on that side, and opens it (§9.3). */
  readonly onInsert: (side: 'before' | 'after') => void;
  /** What the thumbnail's variables are NAMED - a preview shows the name (D5). */
  readonly variableNames: (variable: ExperienceVariable) => string;
}

/** « fondu · normal », « aucune » - the transition as the banner reads it. */
function transitionLabel(transition: PageTransition): string {
  if (transition.animation === 'none') return 'aucune';
  const animation = transition.animation === 'fade' ? 'fondu' : 'glissé';
  return `${animation} · ${SPEED_LABEL[transition.speed]}`;
}

/**
 * Un « + » du bandeau: the label is WRITTEN OUT and not a tooltip on an icon.
 *
 * The plate draws it that way, and the two sides differ by one word - « avant »
 * against « après » - which is exactly the distinction a bare « + » would lose.
 * The scene of #57 can afford the icon alone because its two « + » sit on
 * different edges of a drawn line; here they sit in one row.
 *
 * **And therefore NO bubble** (issue 103). The migration of the native `title`
 * wrapped this button mechanically, which put a drawn bubble over a sentence
 * the button already paints, and posted an `aria-describedby` that made a
 * screen reader say it twice. A written label is the alternative to a tooltip,
 * not a thing to hang one on. Caught in review.
 */
function InsertButton({
  side,
  disabled,
  onInsert,
}: {
  side: 'before' | 'after';
  disabled: boolean;
  onInsert: () => void;
}) {
  const label = side === 'before' ? 'Insérer une page avant' : 'Insérer une page après';
  return (
    <button
      type="button"
      data-testid={side === 'before' ? PAGE_EDITOR_IDS.insertBefore : PAGE_EDITOR_IDS.insertAfter}
      disabled={disabled}
      onClick={onInsert}
      className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md border-2 border-bleu-nuit bg-white px-2.5 py-1.5 font-semibold text-bleu-nuit text-xs hover:bg-bleu-nuit hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${DISABLED_INERT} disabled:cursor-not-allowed`}
    >
      <Plus aria-hidden className="size-3.5 shrink-0" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

export function PageNav({
  neighbours,
  pages,
  pageId,
  feedback = false,
  transition,
  theme,
  format,
  readOnly,
  canInsert,
  onGo,
  onInsert,
  variableNames,
}: PageNavProps) {
  const { previous, next, index, total } = neighbours;
  const here = pages[index];
  /* Between two pages of the thread - or from the protocol page into it - there is
     no transition to read: the edge says « dans le fil » (prolongation.md §6). */
  const nextInThread = edgeInThread(pages, index + 1);

  return (
    <nav
      aria-label="Pages de l’expérience"
      data-testid={PAGE_EDITOR_IDS.nav}
      className="flex flex-wrap items-center justify-between gap-4 rounded-md border-2 border-gray-200 bg-white px-4 py-3"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid={PAGE_EDITOR_IDS.navPrevious}
          disabled={previous === null}
          onClick={() => previous !== null && onGo(previous.id)}
          className={cn(
            'inline-flex min-w-0 max-w-64 cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            previous === null ? 'cursor-not-allowed text-gray-500 hover:bg-transparent' : '',
          )}
        >
          <ChevronLeft aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0">
            <span className="block text-gray-600 text-xs leading-4">Précédente</span>
            <span className="block truncate font-semibold text-bleu-nuit text-sm">
              {previous === null ? 'Début de l’expérience' : previous.name || 'Page sans nom'}
            </span>
          </span>
        </button>

        {/* Actif sur la PREMIÈRE page aussi - voir la tête du fichier. */}
        {readOnly ? null : (
          <InsertButton side="before" disabled={!canInsert} onInsert={() => onInsert('before')} />
        )}
      </div>

      {/*
        The middle column: the rail, then the position line (#140). See the head
        of this file for `min-w-0`, which is what clips the rail's box instead of
        widening the banner, and for `basis-64`, which sends the column to a line
        of its own rather than shrinking the rail to a stamp.
      */}
      <div className="flex min-w-0 grow basis-64 flex-col gap-1">
        <ExperienceRail
          pages={pages}
          selectedPageId={pageId}
          readOnly={readOnly}
          onSelectPage={onGo}
          compact={{ theme, format, variableNames }}
        />

        <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center text-gray-700 text-sm">
          <span data-testid={PAGE_EDITOR_IDS.navPosition} className="font-semibold tabular-nums">
            Page {index + 1} sur {total}
          </span>
          {feedback ? <FeedbackBadge /> : null}
          {here !== undefined && isConversationPage(here) ? <ConversationBadge /> : null}
          {next === null ? null : (
            <>
              <span aria-hidden className="text-gray-400">
                ·
              </span>
              <span data-testid={PAGE_EDITOR_IDS.navTransition} className="text-gray-600">
                {nextInThread ? IN_THREAD_LABEL : `transition ${transitionLabel(transition)}`}
              </span>
            </>
          )}
        </p>
      </div>

      {/*
        The thumbnail is a SIBLING of the button and not its child: `PageReader`
        renders a `<div>`, and a `<button>` takes phrasing content only - nesting
        it would be markup no validator accepts and no reader has to read. It is
        `aria-hidden` anyway, so the name beside it is the whole control.
      */}
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        {readOnly || feedback ? null : (
          <InsertButton side="after" disabled={!canInsert} onInsert={() => onInsert('after')} />
        )}
        <button
          type="button"
          data-testid={PAGE_EDITOR_IDS.navNext}
          disabled={next === null}
          onClick={() => next !== null && onGo(next.id)}
          className={cn(
            'inline-flex min-w-0 max-w-56 cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-right hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            next === null ? 'cursor-not-allowed text-gray-500 hover:bg-transparent' : '',
          )}
        >
          <span className="min-w-0">
            <span className="block text-gray-600 text-xs leading-4">Suivante</span>
            <span className="block truncate font-semibold text-bleu-nuit text-sm">
              {next === null ? 'Fin de l’expérience' : next.name || 'Page sans nom'}
            </span>
          </span>
        </button>
        {next === null ? null : (
          <div
            aria-hidden
            className="h-11 w-16 shrink-0 overflow-hidden rounded-sm border border-gray-200"
          >
            {/* The real reader at a quarter scale - see the head of this file. */}
            <div className="h-44 w-64 origin-top-left scale-25">
              <PageOrThread
                page={next}
                theme={theme}
                format={format}
                mode="static"
                values={{}}
                variableNames={variableNames}
              />
            </div>
          </div>
        )}
        <ChevronRight
          aria-hidden
          className={cn('size-4 shrink-0', next === null ? 'text-gray-400' : '')}
        />
      </div>
    </nav>
  );
}
